import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal - scroll-triggered entrance animation.
 * Respects prefers-reduced-motion.
 */
export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * ChopstickDivider - signature decorative separator.
 * Two thin gold horizontal lines with 3 pairs of diagonal "chopsticks".
 */
export const ChopstickDivider = () => (
  <div
    className="relative w-full overflow-visible"
    style={{ height: '60px' }}
    aria-hidden="true"
    data-testid="chopstick-divider"
  >
    <div
      className="absolute left-0 right-0"
      style={{ top: '22px', height: '1px', background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}
    />
    <div
      className="absolute left-0 right-0"
      style={{ top: '38px', height: '1px', background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}
    />
    <div className="absolute left-1/2 top-1/2 flex" style={{ transform: 'translate(-50%, -50%)', gap: '26px' }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="relative" style={{ width: '12px', height: '80px' }}>
          <span
            className="absolute block"
            style={{ left: '0px', top: '0px', width: '2px', height: '80px', background: '#E8C96A', transform: 'rotate(-35deg)', transformOrigin: 'center' }}
          />
          <span
            className="absolute block"
            style={{ left: '7px', top: '0px', width: '2px', height: '80px', background: '#E8C96A', transform: 'rotate(-35deg)', transformOrigin: 'center' }}
          />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Eyebrow - gold line + uppercase label above section headings.
 */
export const Eyebrow = ({ children, centered = false }) => (
  <div
    className={`flex items-center ${centered ? 'justify-center' : ''}`}
    style={{ gap: '14px' }}
    data-testid="section-eyebrow"
  >
    <span style={{ display: 'block', width: '32px', height: '1px', background: '#C9A84C' }} />
    <span
      className="font-body"
      style={{ fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', color: '#C9A84C', fontWeight: 500 }}
    >
      {children}
    </span>
  </div>
);
